import { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  ChevronRight,
  Phone,
  BookOpen,
  Users,
  Building,
  ArrowLeft,
  UserIcon,
} from "lucide-react";
import { encrypt } from "../../config/aes";
import { useNavigate, Link } from "react-router-dom";
import { authService } from "../../services/authService";
import useToken from "../../hooks/features/auth/useToken";
import { alertError } from "../../helpers/alertError";
import { User } from "../../types/features/User";
import { useGoogleLogin } from "@react-oauth/google";
import { firebaseService } from "../../services/firebaseService";
import { Google, Apple } from "../../components/ui/Icon";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phonenumber: "",
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const { saveToken, saveIzilifeToken } = useToken();
  const navigate = useNavigate();

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const secureData = {
      first_name: encrypt(formData.first_name),
      last_name: encrypt(formData.last_name),
      email: encrypt(formData.email),
      phonenumber: encrypt(formData.phonenumber),
      password: encrypt(formData.password),
      country_id: 1,
      password_confirmation: encrypt(formData.password_confirmation),
      role: "entreprise" as User["role"],
      date_of_birth: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString(),
      fcm_token: null,
      user_promos_codes: null,
      created_at: new Date().toISOString(),
      expired_document_date: new Date().toISOString(),
    };

    try {
      const response = await authService.register(secureData);

      if (response?.statusCode === 200) {
        navigate(`/confirm-account`, {
          state: secureData,
        });
      } else {
        alertError(response?.message || "Échec de l'inscription. Veuillez réessayer.");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Erreur d'inscription. Veuillez réessayer.";
      alertError(errorMessage);
      console.error(error?.response);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);

      const firebaseUser = await firebaseService.getFirebaseUser();

      const fcmToken = "";

      const izilifeResponse = await authService.loginWithGoogle({
        email: encrypt(firebaseUser.user.email || ""),
        id_token: encrypt(firebaseUser.id_token),
        fcm_token: "",
      });

      const izilifeToken = izilifeResponse?.data?.token;
      saveIzilifeToken(izilifeToken);
      console.log("user", {
        email: encrypt(firebaseUser.user.email || ""),
        id_token: encrypt(firebaseUser.id_token),
        fcm_token: "",
      });

      const loginResponse = await authService.loginWithIzilifeToken(izilifeToken);

      saveToken(loginResponse?.data?.token);
      navigate("/dashboard");
    } catch (error: any) {
      console.error(error);
    }
  };
  const handleAppleLogin = async () => {
    try {
      setIsAppleLoading(true);
      // À implémenter avec Apple Sign In
      // const response = await authService.loginWithApple({ ... });
      alertError("Connexion Apple en cours de développement");
    } catch (error: any) {
      alertError(error?.response?.data?.message || "Erreur de connexion Apple");
    } finally {
      setIsAppleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white font-sans antialiased select-none">
      {/* PANEL GAUCHE */}
      <div className="relative hidden w-1/2 bg-[#031B4E] lg:block overflow-hidden">
        {/* Trame de fond subtile */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Effet lumineux dynamique au curseur */}
        <div
          className="absolute w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none transition-all duration-500 ease-out"
          style={{
            top: `${mousePosition.y - 300}px`,
            left: `${mousePosition.x - 300}px`,
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-16 text-white">
          {/* Header Logo */}
          <Link to={"/"} className="flex items-center gap-2">
            <img src="/logo.png" alt="IZI Call Center" className="h-12 w-auto" />
            <div>
              <div className="text-xl font-black tracking-wider leading-none">
                <span className="text-white">Izi</span> <span className="text-[#FFB800]">CALL CENTER</span>
              </div>
              <div className="text-[9px] tracking-[0.25em] text-gray-300 font-medium uppercase mt-0.5">
                Connecter • Assister • Satisfaire
              </div>
            </div>
          </Link>

          {/* Contenu principal */}
          <div className="max-w-xl space-y-8">
            <div className="space-y-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300 border border-blue-500/20">
                Étape 1 sur 2
              </span>
              <h1 className="text-5xl font-extrabold tracking-tight leading-[1.15] text-white">
                Centralisez et <br />
                <span className="text-blue-400">optimisez</span> votre relation client.
              </h1>
              <p className="text-lg text-slate-300/90 leading-relaxed font-light">
                Rejoignez la plateforme de gestion la plus innovante. Équipez vos conseillers et pilotez votre activité
                en temps réel.
              </p>
            </div>

            {/* Liste des arguments clés */}
            <div className="space-y-4 pt-4 border-t border-white/5">
              <div className="flex items-start gap-4">
                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                  <Building className="h-3.5 w-3.5" />
                </div>
                <p className="text-sm text-slate-300 font-medium">Gestion multi-agents pour votre structure</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                  <Users className="h-3.5 w-3.5" />
                </div>
                <p className="text-sm text-slate-300 font-medium">Configuration de conseillers en quelques clics</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                  <BookOpen className="h-3.5 w-3.5" />
                </div>
                <p className="text-sm text-slate-300 font-medium">Base de connaissances collaborative intégrée</p>
              </div>
            </div>
          </div>

          {/* Footer avec liens légaux */}
          <div className="flex items-center justify-between text-xs border-t border-white/5 pt-6">
            <div className="flex items-center gap-4">
              <Link to="/mentions-legales" className="text-slate-400/70 hover:text-white transition-colors">
                Mentions légales
              </Link>
              <span className="text-slate-500/30">•</span>
              <Link to="/confidentialite" className="text-slate-400/70 hover:text-white transition-colors">
                Confidentialité
              </Link>
              <span className="text-slate-500/30">•</span>
              <Link to="/cgu" className="text-slate-400/70 hover:text-white transition-colors">
                CGU
              </Link>
            </div>
            <span className="text-slate-500/50">© 2026</span>
          </div>
        </div>
      </div>

      {/* PANEL DROIT */}
      <div className="flex w-full flex-col justify-between px-6 py-10 lg:w-1/2 sm:px-16 lg:px-24 bg-white">
        {/* Logo mobile uniquement */}
        <div className="flex items-center justify-between lg:hidden mb-8">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Izi CallCenter" className="h-8 w-auto" />
            <span className="text-lg font-bold text-[#031B4E]">Izi CallCenter</span>
          </div>
        </div>

        {/* Spacer pour centrer verticalement en desktop */}
        <div className="hidden lg:block h-8" />

        {/* Formulaire */}
        <div className="mx-auto w-full max-w-md space-y-8 my-auto">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight text-[#031B4E]">Créer un compte</h2>
            <p className="text-sm text-slate-500 font-light">
              Inscrivez votre entreprise et commencez dès aujourd'hui.
            </p>
          </div>

          {/* Boutons Google et Apple */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => handleGoogleLogin()}
              disabled={isGoogleLoading}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-4 focus:ring-slate-200/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGoogleLoading ? (
                <div className="h-4 w-4 border-2 border-slate-700 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Google className="h-5 w-5" />
              )}
              <span>Utiliser mon compte Google</span>
            </button>

            <button
              type="button"
              onClick={handleAppleLogin}
              disabled={isAppleLoading}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-4 focus:ring-slate-200/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAppleLoading ? (
                <div className="h-4 w-4 border-2 border-slate-700 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Apple className="h-5 w-5" />
              )}
              <span>Utiliser mon compte Apple</span>
            </button>
          </div>

          {/* Séparateur */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-slate-400">ou inscrivez-vous avec votre email</span>
            </div>
          </div>

          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Grille Nom / Prénom */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="first_name" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                  Prénom
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                    placeholder="Jean"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="last_name" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                  Nom
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                    placeholder="Dupont"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                Adresse email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  placeholder="contact@entreprise.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Téléphone */}
            <div className="space-y-1.5">
              <label htmlFor="phonenumber" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                Téléphone <span className="text-slate-400 lowercase font-normal">(optionnel)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="phonenumber"
                  name="phonenumber"
                  type="tel"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  placeholder="+33 6 12 34 56 78"
                  value={formData.phonenumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-xs font-semibold text-slate-700 tracking-wide uppercase">
                  Mot de passe
                </label>
                <span className="text-[11px] text-slate-400 font-normal">8 caractères min.</span>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-10 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirmation Mot de passe */}
            <div className="space-y-1.5">
              <label
                htmlFor="password_confirmation"
                className="text-xs font-semibold text-slate-700 tracking-wide uppercase"
              >
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-10 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  placeholder="••••••••"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#031B4E] px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/10 hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-2"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 font-medium">Création du compte...</span>
                </div>
              ) : (
                <>
                  <span>Continuer</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            {/* Redirection Login */}
            <div className="text-center pt-2">
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#031B4E] transition-colors gap-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Déjà inscrit ? Se connecter
              </Link>
            </div>
          </form>
        </div>

        {/* Footer Droite */}
        <div className="text-center text-xs text-slate-400 mt-8">© 2026 Izi CallCenter. Tous droits réservés.</div>
      </div>
    </div>
  );
};

export default RegisterPage;
